import { useState, useEffect, useCallback } from 'react';
import { Layout, Button, Space, Avatar, Typography, Dropdown, App } from 'antd';
import type { MenuProps } from 'antd';
import { UserSwitchOutlined, LogoutOutlined, UserOutlined } from '@ant-design/icons';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useScrollToTopWindow } from '../hooks/useScrollToTop';
import { useOperationStore, useCurrentOperator } from '../stores/operationStore';
import { useAuthStore } from '../stores/authStore';
import { trpc, trpcUntypedClient, queryClient } from '../lib/api';
import { OperatorSelectionModal } from '../components/operation/OperatorSelectionModal';
import { HelpButton } from '../components/help/HelpButton';
import { HelpModal } from '../components/help/HelpModal';
import { useResponsive, LAYOUT_CONSTANTS } from '../hooks/useResponsive';
import { DevRouteInfo } from '../components/common/DevRouteInfo';

const { Header, Content } = Layout;
const { Text } = Typography;

/**
 * 共有ユーザー専用レイアウト
 * タブレット操作に最適化された大きなボタンとシンプルなUI
 */
export function SharedUserLayout() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const currentOperator = useCurrentOperator();
  const { user } = useAuthStore();
  const { sessionToken, sharedUserId, clearOperationMode, isOperationMode } = useOperationStore();
  const [operatorModalVisible, setOperatorModalVisible] = useState(false);

  // レスポンシブ対応
  const { isMobile } = useResponsive();
  const headerHeight = isMobile ? LAYOUT_CONSTANTS.HEADER_HEIGHT_MOBILE : 80;
  const contentPadding = isMobile ? LAYOUT_CONSTANTS.CONTENT_PADDING_MOBILE : 32;

  // ページ遷移時にスクロール位置をトップにリセット
  useScrollToTopWindow();

  // 現在のパスに応じたデフォルトヘルプトピックを決定
  const getDefaultHelpTopicId = useCallback((): string | undefined => {
    const path = location.pathname;

    // 共有ユーザー向けのページ
    if (path.startsWith('/shared-app/maintenance')) return 'maintenance-calendar';
    if (path.startsWith('/shared-app/emergency-works')) return 'emergency-overview';
    if (path.startsWith('/shared-app/my-tasks')) return 'maintenance-my-tasks';
    if (path === '/shared-app/dashboard') return 'getting-started-dashboard';

    return undefined;
  }, [location.pathname]);

  // 初回アクセス時、オペレーションモードでない場合はモーダルを表示
  useEffect(() => {
    if (!isOperationMode && !currentOperator) {
      setOperatorModalVisible(true);
    }
  }, [isOperationMode, currentOperator]);

  const endOperationMutation = trpc.userOperator.endOperation.useMutation({
    onSuccess: () => {
      // React Query のキャッシュを完全にクリア（重要：前のユーザーのデータを残さない）
      // invalidate() ではなく clear() を使用 - invalidate() は stale マークのみで実データは残る
      queryClient.clear();
      message.success('オペレーションモードを終了しました');
      clearOperationMode();
      navigate('/login');
    },
    onError: (error) => {
      message.error(`ログアウトに失敗しました: ${error.message}`);
    },
  });

  // セッション検証（定期的にチェック）
  useEffect(() => {
    if (!sessionToken) return;

    const validateSession = async () => {
      try {
        await trpcUntypedClient.query('userOperator.validateOperation', { sessionToken });
      } catch {
        message.error('セッションが無効です。再度ログインしてください。');
        clearOperationMode();
        navigate('/login');
      }
    };

    // 初回実行
    validateSession();

    // 5分ごとにセッション検証
    const interval = setInterval(validateSession, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [sessionToken, clearOperationMode, navigate, message]);

  const handleOperatorChange = () => {
    setOperatorModalVisible(true);
  };

  const handleLogout = async () => {
    if (!sessionToken) return;

    try {
      await endOperationMutation.mutateAsync({ sessionToken });
    } catch {
      // エラーハンドリングはmutationのonErrorで行う
    }
  };

  // オペレータードロップダウンメニュー
  const operatorMenuItems: MenuProps['items'] = [
    {
      key: 'change',
      label: 'オペレーター変更',
      icon: <UserSwitchOutlined />,
      onClick: handleOperatorChange,
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      label: 'ログアウト',
      icon: <LogoutOutlined />,
      danger: true,
      onClick: handleLogout,
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh', background: '#f0f2f5' }}>
      <Header
        style={{
          background: '#fff',
          padding: `0 ${contentPadding}px`,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: headerHeight,
        }}
      >
        <Space size={isMobile ? 'small' : 'large'}>
          <Text strong style={{ fontSize: isMobile ? 16 : 24 }}>
            {isMobile ? '工場' : '工場タブレット'}
          </Text>

          {currentOperator && !isMobile && (
            <Button
              type="primary"
              size="large"
              icon={<UserSwitchOutlined />}
              onClick={handleOperatorChange}
              style={{
                height: '56px',
                fontSize: '18px',
                padding: '0 32px',
              }}
            >
              オペレーター変更
            </Button>
          )}
        </Space>

        {currentOperator && (
          <Dropdown menu={{ items: operatorMenuItems }} trigger={['click']}>
            <Space
              style={{
                cursor: 'pointer',
                padding: isMobile ? '8px 12px' : '12px 24px',
                borderRadius: '8px',
                background: '#f5f5f5',
                transition: 'background 0.3s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#e6e6e6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#f5f5f5';
              }}
            >
              <Avatar
                size={isMobile ? 36 : 48}
                icon={<UserOutlined />}
                style={{ background: '#1890ff' }}
              >
                {currentOperator.name[0]}
              </Avatar>
              {!isMobile && (
                <div style={{ lineHeight: '1.5' }}>
                  <Text strong style={{ fontSize: 18, display: 'block' }}>
                    {currentOperator.name}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 14 }}>
                    オペレーションモード
                  </Text>
                </div>
              )}
            </Space>
          </Dropdown>
        )}
      </Header>

      <Content
        style={{
          padding: contentPadding,
          background: '#f0f2f5',
          minHeight: `calc(100vh - ${headerHeight}px)`,
        }}
      >
        <div
          style={{
            background: '#fff',
            borderRadius: isMobile ? '4px' : '8px',
            padding: contentPadding,
            minHeight: '100%',
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
          }}
        >
          <Outlet />
        </div>
      </Content>

      {/* 開発環境でのみ表示されるファイルパス情報 */}
      <DevRouteInfo />

      {/* オペレーター選択モーダル */}
      {(sharedUserId || user?.id) && (
        <OperatorSelectionModal
          visible={operatorModalVisible}
          sharedUserId={sharedUserId || user?.id || ''}
          onClose={() => setOperatorModalVisible(false)}
          onOperatorSelected={(operatorId, operatorName) => {
            setOperatorModalVisible(false);
            message.success(`${operatorName}さんでログインしました`);
          }}
        />
      )}

      {/* ヘルプボタンとモーダル */}
      <HelpButton position="fixed" topicId={getDefaultHelpTopicId()} />
      <HelpModal />
    </Layout>
  );
}
